import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import * as geometryJsonUtils from '@arcgis/core/geometry/support/jsonUtils';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import type { Geometry } from 'geojson';
import type { ArcgisMapView } from './arcgisView';
import {
  attributeStoredFields,
  storedFieldAttributes,
  storedFieldsPopupTemplate,
  type StoredField,
} from './locationPopup';

const GEOJSON_GEOMETRY_TYPES = new Set([
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
]);

/** Turn GeoJSON or an ArcGIS geometry into something `goTo` can frame. */
function geometryFromPayload(geometry: unknown): __esri.GeometryUnion | null {
  if (!geometry || typeof geometry !== 'object') return null;

  const record = geometry as { type?: string; toJSON?: () => unknown };
  try {
    if (typeof record.toJSON === 'function') {
      return (
        geometryJsonUtils.fromJSON(
          record.toJSON() as __esri.GeometryProperties
        ) ?? null
      );
    }

    if (record.type && GEOJSON_GEOMETRY_TYPES.has(record.type)) {
      const arcgisJson = geojsonToArcGIS(geometry as Geometry) as Record<
        string,
        unknown
      >;
      return (
        geometryJsonUtils.fromJSON({
          ...arcgisJson,
          spatialReference: { wkid: 4326 },
        } as __esri.GeometryProperties) ?? null
      );
    }

    return (
      geometryJsonUtils.fromJSON(geometry as __esri.GeometryProperties) ?? null
    );
  } catch {
    return null;
  }
}

/** Resolve a map popup anchor from a graphic geometry. */
export function getPopupLocationFromGeometry(
  geometry: __esri.GeometryUnion | null | undefined
): __esri.Point | null {
  if (!geometry) return null;
  if (geometry.type === 'point') return geometry as __esri.Point;
  if (geometry.extent?.center) return geometry.extent.center;
  return null;
}

/**
 * Zoom the map to a feature graphic and open its ArcGIS popup (same surface as
 * clicking the feature on the map when popupTemplate is set).
 */
export async function focusFeatureOnMap({
  mapView,
  graphic,
}: {
  mapView: ArcgisMapView | null;
  graphic: __esri.Graphic;
}): Promise<void> {
  if (!mapView || !graphic.geometry) return;

  const isPoint = graphic.geometry.type === 'point';
  try {
    await mapView.goTo(
      isPoint
        ? { target: graphic.geometry, zoom: 14 }
        : { target: graphic.geometry },
      { duration: 500 }
    );
  } catch {
    return;
  }

  const location = getPopupLocationFromGeometry(graphic.geometry);
  if (!location || !mapView.popup) return;

  try {
    await mapView.openPopup({
      features: [graphic],
      location,
    });
  } catch {
    // The view moved. A popup that cannot resolve a screen point can fail closed.
  }
}

export function buildStoredFieldsGraphic(params: {
  geometry: unknown;
  title?: string | null;
  fields: StoredField[];
}): Graphic | null {
  const geom = geometryFromPayload(params.geometry);
  if (!geom) return null;
  const title = params.title?.trim() || 'Feature';
  return new Graphic({
    geometry: geom,
    attributes: {
      name: title,
      ...storedFieldAttributes(params.fields),
    },
    popupTemplate: storedFieldsPopupTemplate(title, params.fields),
  });
}

export function applyStoredAttributePopup(
  graphic: __esri.Graphic,
  title?: string | null
) {
  const attributes = (graphic.attributes ?? {}) as Record<string, unknown>;
  const fields = attributeStoredFields(attributes);
  if (fields.length === 0) return;
  const popupTitle =
    title?.trim() ||
    (typeof attributes.name === 'string' && attributes.name) ||
    (typeof attributes.Name === 'string' && attributes.Name) ||
    'Feature';
  graphic.attributes = {
    ...attributes,
    name: popupTitle,
    ...storedFieldAttributes(fields),
  };
  graphic.popupTemplate = storedFieldsPopupTemplate(popupTitle, fields);
}

const FALLBACK_POPUP_TEMPLATE = new PopupTemplate({
  title: '{name}',
  content: [
    {
      type: 'fields',
      fieldInfos: [
        { fieldName: 'name', label: 'Name' },
        { fieldName: 'incidentName', label: 'Incident' },
        { fieldName: 'resourceType', label: 'Resource Type' },
        { fieldName: 'description', label: 'Description' },
      ],
    },
  ],
});

/** Mirrors the polygon popup in syncInternalLayers: no raw resource type. */
const FALLBACK_POLYGON_POPUP_TEMPLATE = new PopupTemplate({
  title: '{name}',
  content: [
    {
      type: 'fields',
      fieldInfos: [
        { fieldName: 'name', label: 'Name' },
        { fieldName: 'incidentName', label: 'Incident' },
        { fieldName: 'description', label: 'Description' },
      ],
    },
  ],
});

/** Build a temporary graphic from citation / list fallback payload. */
export function buildFallbackFeatureGraphic(params: {
  geometry: unknown;
  attributes?: Record<string, unknown> | null;
  title?: string | null;
}): Graphic | null {
  const { geometry, attributes, title } = params;
  if (!geometry) return null;

  try {
    const geom = geometryFromPayload(geometry);

    if (!geom) return null;

    const attrs: Record<string, unknown> = {
      ...(attributes ?? {}),
      name:
        title ||
        (attributes?.name as string | undefined) ||
        (attributes?.Name as string | undefined) ||
        'Feature',
    };

    return new Graphic({
      geometry: geom,
      attributes: attrs,
      popupTemplate:
        attrs.resourceType === 'customPolygon'
          ? FALLBACK_POLYGON_POPUP_TEMPLATE
          : FALLBACK_POPUP_TEMPLATE,
    });
  } catch {
    return null;
  }
}

/** Convenience when only a lon/lat is known (e.g. pending add-layer objects). */
export function buildPointFeatureGraphic(params: {
  longitude: number;
  latitude: number;
  title?: string | null;
  attributes?: Record<string, unknown> | null;
}): Graphic {
  return new Graphic({
    geometry: new Point({
      longitude: params.longitude,
      latitude: params.latitude,
    }),
    attributes: {
      ...(params.attributes ?? {}),
      name: params.title || 'Feature',
    },
    popupTemplate: FALLBACK_POPUP_TEMPLATE,
  });
}
